import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface FilterComponentInfo {
    filterId: string;
    initialDefaultOpen: boolean;
    currentDefaultOpen: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class FilterStateService {
    private filtersModalOpened = false;
    private userHasInteractedWithFilters = false;
    private filterComponents = new Map<string, FilterComponentInfo>();
    private currentCategoryId: string | number | null = null;

    // Observable to notify components when global state changes
    private stateChanged$ = new BehaviorSubject<boolean>(false);

    /**
     * Get the state change observable
     */
    getStateChanges() {
        return this.stateChanged$.asObservable();
    }

    /**
     * Set the current category and reset state if category has changed
     */
    setCategoryId(categoryId: string | number | null): void {
        if (this.currentCategoryId !== categoryId) {
            const previousCategoryId = this.currentCategoryId;
            this.currentCategoryId = categoryId;

            if (previousCategoryId !== null) {
                // Category changed - reset all state for the new category
                this.resetState();
            } else {
                // First time setting category
            }
        }
    }

    /**
     * Get the current category ID
     */
    getCurrentCategoryId(): string | number | null {
        return this.currentCategoryId;
    }

    /**
     * Check if the filters modal has been opened
     */
    hasFiltersModalBeenOpened(): boolean {
        return this.filtersModalOpened;
    }

    /**
     * Mark that the filters modal has been opened
     */
    setFiltersModalOpened(): void {
        if (!this.filtersModalOpened) {
            this.filtersModalOpened = true;
        }
    }

    /**
     * Check if user has interacted with filters
     */
    hasUserInteractedWithFilters(): boolean {
        return this.userHasInteractedWithFilters;
    }

    /**
     * Register a filter component
     */
    registerFilterComponent(filterId: string, initialDefaultOpen: boolean): void {
        const existingInfo = this.filterComponents.get(filterId);

        if (!existingInfo) {
            // First time registration - use initial state
            this.filterComponents.set(filterId, {
                filterId,
                initialDefaultOpen,
                currentDefaultOpen: this.userHasInteractedWithFilters ? false : initialDefaultOpen
            });
        } else {
            // Component recreated - preserve current state but update initial if needed
            existingInfo.initialDefaultOpen = initialDefaultOpen;
            existingInfo.currentDefaultOpen = false;
        }
    }

    /**
     * Get the current default open state for a filter
     */
    getFilterDefaultOpenState(filterId: string): boolean {
        const info = this.filterComponents.get(filterId);
        return info ? info.currentDefaultOpen : false; // Default to true if not found
    }

    /**
     * Check if any currently open filter was not initially set to default open
     */
    checkForNonDefaultFilterInteraction(openFilterIds: string[]): boolean {
        for (const filterId of openFilterIds) {
            const info = this.filterComponents.get(filterId);

            if (info && !info.initialDefaultOpen) {
                return true;
            }
        }

        return false;
    }

    /**
     * Mark that user has interacted with filters and update all filter states
     */
    setUserHasInteractedWithFilters(): void {
        if (!this.userHasInteractedWithFilters) {
            this.userHasInteractedWithFilters = true;

            // Update all filter components to collapsed by default
            this.filterComponents.forEach(info => {
                info.currentDefaultOpen = false;
            });

            // Notify all components about the state change
            this.stateChanged$.next(true);
        }
    }

    /**
     * Reset all state - useful for testing or navigation
     * Note: This preserves the current category ID
     */
    resetState(): void {
        this.filtersModalOpened = false;
        this.userHasInteractedWithFilters = false;
        this.filterComponents.clear();
        this.stateChanged$.next(false);
    }

    /**
     * Complete reset including category - useful for testing
     */
    resetAllState(): void {
        this.resetState();
        this.currentCategoryId = null;
    }

    /**
     * Get debug information about current state
     */
    getDebugInfo() {
        return {
            currentCategoryId: this.currentCategoryId,
            filtersModalOpened: this.filtersModalOpened,
            userHasInteractedWithFilters: this.userHasInteractedWithFilters,
            registeredFilters: Array.from(this.filterComponents.entries()).map(([id, info]) => ({
                id,
                initialDefaultOpen: info.initialDefaultOpen,
                currentDefaultOpen: info.currentDefaultOpen
            }))
        };
    }
}
